import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import { relativeTime, titleCase } from '../../lib/format'
import type { AppNotification, ListResponse } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton,
} from '../../components/ui'
import { CheckIcon, InboxIcon } from '../../components/icons'
import './dashboard.css'

export default function Notifications() {
  const qc = useQueryClient()
  const { user } = useAuth()
  // Leaders and miners have separate notification queues behind the same UI.
  const base = portalFor(user) === 'leader' ? '/leader' : '/miner'

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['notifications', base],
    queryFn: () => api.get<ListResponse<AppNotification>>(`${base}/notifications`),
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['notifications'] })
    void qc.invalidateQueries({ queryKey: ['miner', 'dashboard'] })
    void qc.invalidateQueries({ queryKey: ['leader', 'dashboard'] })
  }

  const markOne = useMutation({
    mutationFn: (id: string) => api.post(`${base}/notifications/${id}/read`),
    onSuccess: invalidate,
  })
  const markAll = useMutation({
    mutationFn: () => api.post(`${base}/notifications/mark-all-read`),
    onSuccess: invalidate,
  })

  const items = data?.items ?? []
  const unread = data?.unread ?? 0

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Notifications</h1>
          <p className="page-head__sub">
            {unread > 0 ? `${unread} unread` : 'You are all caught up.'}
          </p>
        </div>
        {unread > 0 && (
          <Button variant="secondary" icon={<CheckIcon size={16} />}
                  loading={markAll.isPending} onClick={() => markAll.mutate()}>
            Mark all read
          </Button>
        )}
      </div>

      <Card>
        <CardHeader title="All notifications"
                    sub={items.length ? `${items.length} total` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}>
              <Alert tone="danger">{(error as Error).message}</Alert>
            </div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="76%" /><Skeleton h={20} w="60%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<InboxIcon />} title="No notifications"
                        body="Updates about safety reports, training, inspections and approvals arrive here." />
          ) : (
            <div className="feed">
              {items.map((n, i) => (
                <div key={n.id ?? i} className={`feed__item${n.read ? '' : ' feed__item--unread'}`}>
                  <span className={`feed__dot${n.read ? ' feed__dot--read' : ''}`} aria-hidden />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="feed__title">
                      {n.title ?? titleCase(n.type) ?? 'Notification'}
                    </div>
                    {(n.body ?? n.message) && (
                      <div className="feed__body">{n.body ?? n.message}</div>
                    )}
                    <div className="feed__time">{relativeTime(n.created_at ?? n.timestamp)}</div>
                  </div>
                  {!n.read && n.id && (
                    <Button size="sm" variant="ghost"
                            disabled={markOne.isPending}
                            onClick={() => markOne.mutate(n.id!)}>
                      Mark read
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
